import React, { useEffect, useState } from 'react';
import { Apple } from 'lucide-react';
import { fetchFoodLogImageObjectUrl, getFoodLogImageSrc } from '../../services/clientApi';

export default function FoodLogPhoto({
  log,
  alt = '',
  className = '',
  fallback = null,
  onClick,
  language = 'english',
}) {
  const he = language === 'hebrew';
  const [src, setSrc] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setSrc(null);

    const remote = getFoodLogImageSrc(log);
    if (!remote) {
      setStatus('empty');
      return undefined;
    }

    setStatus('loading');

    (async () => {
      try {
        if (log?.id) {
          objectUrl = await fetchFoodLogImageObjectUrl(log.id);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          setSrc(objectUrl);
          setStatus('success');
          return;
        }
        if (!cancelled) {
          setSrc(remote);
          setStatus('success');
        }
      } catch (err) {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [log?.id, log?.has_image, log?.image_url]);

  if (status === 'empty') {
    return fallback || null;
  }

  if (status === 'loading') {
    return (
      <div
        className={`terminal-skeleton w-full h-full min-h-[12rem] ${className}`}
        aria-busy="true"
        aria-label={he ? 'טוען תמונת ארוחה' : 'Loading meal photo'}
      />
    );
  }

  if (status === 'error' || !src) {
    return fallback || (
      <div className="w-full min-h-[12rem] flex flex-col items-center justify-center gap-2 bg-moss dark:bg-moss-dark text-ink-muted">
        <Apple size={28} className="text-forest-ondark/50" aria-hidden="true" />
        <span className="text-[11px]">{he ? 'לא ניתן לטעון את התמונה' : 'Photo could not load'}</span>
      </div>
    );
  }

  const img = (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setStatus('error')}
    />
  );

  if (!onClick) return img;

  return (
    <button
      type="button"
      onClick={() => onClick(src)}
      className="block w-full text-start cursor-pointer hover:opacity-90 active:scale-[0.99] transition-opacity duration-[120ms] ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest-ondark"
      aria-label={he ? 'הגדל תמונת ארוחה' : 'Expand meal photo'}
    >
      {img}
    </button>
  );
}
